import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

import { authService } from '../../utils/authService';
import { strapiService } from '../../utils/api';
import AlertService from '../../utils/alertService';
import SystemUsersList from './components/SystemUsersList';
import CreateUserModal from './components/CreateUserModal';
import UserActionsToolbar from './components/UserActionsToolbar';
import {
  Button, Card, Counters, SearchInput, Select, Skel,
} from '../../components/app2';

/**
 * Super Administradores (rediseño jul-2026).
 *
 * Conserva toda la operación: alta con correo de bienvenida, restablecer
 * contraseña, borrado individual y masivo, y las protecciones de siempre (nadie
 * se borra ni se restablece a sí mismo). Cambia el molde: contadores arriba en
 * vez de tres líneas de texto sueltas, filtros dentro de la tarjeta del listado
 * y tabla del sistema.
 *
 * Dos correcciones de fondo mientras se migraba:
 *  1. "Seleccionar todos" incluía al propio usuario, que el backend rechaza con
 *     403 — ahora queda fuera de la selección, como su casilla individual.
 *  2. El borrado masivo abortaba a la primera falla y dejaba la lista mintiendo
 *     (los ya borrados seguían en pantalla). Ahora se informa cuántos se
 *     borraron y cuántos fallaron, y la lista refleja lo que de verdad pasó.
 */

const STATUS_OPTIONS = [
  { value: 'all', label: 'Todos los estados' },
  { value: 'active', label: 'Activos' },
  { value: 'inactive', label: 'Inactivos' },
];

const SORT_OPTIONS = [
  { value: 'name', label: 'Nombre A-Z' },
  { value: 'name_desc', label: 'Nombre Z-A' },
  { value: 'email', label: 'Correo A-Z' },
  { value: 'created', label: 'Más recientes' },
  { value: 'last_login', label: 'Último acceso' },
];

const SystemUserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);

  useEffect(() => {
    loadSystemUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSystemUsers = async () => {
    setLoading(true);
    try {
      const response = await strapiService?.get('admin/super-admins');

      if (response?.data) {
        const transformedUsers = response?.data?.map(user => ({
          id: user?.id,
          documentId: user?.documentId,
          email: user?.email,
          first_name: user?.first_name,
          last_name: user?.last_name,
          user_type: user?.user_type,
          is_active: user?.is_active,
          must_change_password: user?.must_change_password,
          last_login: user?.last_login,
          createdAt: user?.createdAt,
          blocked: user?.blocked,
          confirmed: user?.confirmed
        }));

        setUsers(transformedUsers);
      }
    } catch (error) {
      console.error('❌ Error loading Super Admin users:', error);
      await AlertService?.error(
        'Error al cargar Super Admins',
        'No se pudieron cargar los usuarios Super Admin. Por favor, intente nuevamente.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort users
  const filteredAndSortedUsers = users?.filter(user => {
    const matchesSearch =
      user?.first_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.last_name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
      user?.email?.toLowerCase()?.includes(searchTerm?.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && user?.is_active) ||
      (statusFilter === 'inactive' && !user?.is_active);

    return matchesSearch && matchesStatus;
  })?.sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return `${a?.first_name} ${a?.last_name}`?.localeCompare(`${b?.first_name} ${b?.last_name}`);
      case 'name_desc':
        return `${b?.first_name} ${b?.last_name}`?.localeCompare(`${a?.first_name} ${a?.last_name}`);
      case 'email':
        return a?.email?.localeCompare(b?.email);
      case 'created':
        return new Date(b?.createdAt) - new Date(a?.createdAt);
      case 'last_login': {
        const aLogin = a?.last_login ? new Date(a?.last_login) : new Date(0);
        const bLogin = b?.last_login ? new Date(b?.last_login) : new Date(0);
        return bLogin - aLogin;
      }
      default:
        return 0;
    }
  });

  // Handle password reset
  const handleResetPassword = async (userId) => {
    const userToReset = users?.find(user => user?.id === userId);
    const userName = userToReset
      ? `${userToReset?.first_name} ${userToReset?.last_name}`
      : 'este usuario';

    const confirmed = await AlertService?.confirm(
      'Restablecer contraseña',
      `¿Está seguro de que desea restablecer la contraseña de ${userName}? Se enviará un correo con las nuevas credenciales.`,
      {
        confirmText: 'Sí, restablecer',
        cancelText: 'Cancelar',
        confirmColor: '#f59e0b'
      }
    );

    if (!confirmed) return;

    try {
      AlertService?.loading('Restableciendo contraseña...', 'Por favor espere mientras se procesa la solicitud.');

      const response = await strapiService?.post(`admin/super-admins/${userId}/reset-password`);

      AlertService?.close();

      if (response?.status === 200 || response?.data) {
        await AlertService?.success(
          'Contraseña restablecida',
          'Se envió un email al usuario con sus nuevas credenciales.'
        );
      }
    } catch (error) {
      console.error('❌ Error resetting password:', error);
      AlertService?.close();

      if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          error?.response?.data?.error?.message || 'No puedes resetear tu propia contraseña'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Usuario no encontrado',
          'El usuario especificado no existe en el sistema'
        );
      } else {
        await AlertService?.error(
          'Error al restablecer contraseña',
          'Ocurrió un error inesperado. Por favor, intente nuevamente.'
        );
      }
    }
  };

  // Handle user creation
  const handleCreateUser = async (userData) => {
    setIsCreatingUser(true);

    try {
      AlertService?.loading('Creando Super Admin...', 'Por favor espere mientras se procesa la creación del usuario.');

      await strapiService?.post('admin/super-admins', {
        email: userData?.email,
        password: userData?.password,
        firstName: userData?.firstName,
        lastName: userData?.lastName
      });

      // Reload users to get updated list
      await loadSystemUsers();
      setShowCreateModal(false);

      AlertService?.close();

      const userName = `${userData?.firstName} ${userData?.lastName}`;
      await AlertService?.success(
        'Super Admin creado exitosamente',
        `${userName} ha sido agregado al sistema. Se ha enviado un email de bienvenida con las credenciales.`,
        { autoClose: true }
      );
    } catch (error) {
      console.error('❌ Error creating Super Admin:', error);
      AlertService?.close();

      let errorTitle = 'Error al crear Super Admin';
      let errorMessage = 'Ocurrió un error inesperado. Por favor, intente nuevamente.';

      if (error?.response?.status === 400) {
        errorTitle = 'Datos incorrectos';
        errorMessage = error?.response?.data?.error?.message || 'Error en los datos proporcionados o el usuario ya existe';
      } else if (error?.response?.status === 403) {
        errorTitle = 'Sin permisos';
        errorMessage = 'No tienes permisos para crear Super Admins';
      } else if (error?.response?.data?.message) {
        errorMessage = error?.response?.data?.message;
      }

      await AlertService?.error(errorTitle, errorMessage);
    } finally {
      setIsCreatingUser(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (userId) => {
    const userToDelete = users?.find(user => user?.id === userId);
    const userName = userToDelete
      ? `${userToDelete?.first_name} ${userToDelete?.last_name}`
      : 'este usuario';

    const confirmed = await AlertService?.confirmDelete(
      userName,
      'Se eliminarán todos los permisos y accesos asociados a este Super Admin.'
    );

    if (!confirmed) return;

    try {
      AlertService?.loading('Eliminando Super Admin...', 'Por favor espere mientras se procesa la eliminación.');

      await strapiService?.delete(`admin/super-admins/${userId}`);

      // Remove from local state
      setUsers(prevUsers => prevUsers?.filter(user => user?.id !== userId));
      setSelectedUsers(prevSelected => prevSelected?.filter(id => id !== userId));

      AlertService?.close();
      await AlertService?.successToast('Super Admin eliminado exitosamente');
    } catch (error) {
      console.error('❌ Error deleting Super Admin:', error);
      AlertService?.close();

      if (error?.response?.status === 403) {
        await AlertService?.error(
          'Sin permisos',
          error?.response?.data?.error?.message || 'No puedes eliminarte a ti mismo'
        );
      } else if (error?.response?.status === 404) {
        await AlertService?.error(
          'Usuario no encontrado',
          'El usuario especificado no existe en el sistema'
        );
      } else {
        await AlertService?.error(
          'Error al eliminar Super Admin',
          'No se pudo completar la eliminación. Por favor, intente nuevamente.'
        );
      }
    }
  };

  // Handle bulk operations
  const handleBulkDelete = async () => {
    if (selectedUsers?.length === 0) return;

    const confirmed = await AlertService?.confirmDelete(
      `${selectedUsers?.length} Super Admin${selectedUsers?.length > 1 ? 's' : ''}`,
      'Se eliminarán todos los permisos y accesos asociados a estos Super Admins.'
    );

    if (!confirmed) return;

    AlertService?.loading(
      'Eliminando Super Admins...',
      `Procesando eliminación de ${selectedUsers?.length} Super Admin${selectedUsers?.length > 1 ? 's' : ''}.`
    );

    // Se intentan TODAS aunque alguna falle, y se reporta el resultado real.
    const borrados = [];
    const fallidos = [];
    for (const userId of selectedUsers) {
      try {
        await strapiService?.delete(`admin/super-admins/${userId}`);
        borrados.push(userId);
      } catch (error) {
        console.error('❌ Error deleting Super Admin', userId, error);
        fallidos.push(userId);
      }
    }

    setUsers(prevUsers => prevUsers?.filter(user => !borrados?.includes(user?.id)));
    setSelectedUsers(fallidos);

    AlertService?.close();

    if (fallidos.length === 0) {
      await AlertService?.successToast(
        `${borrados.length} Super Admin${borrados.length > 1 ? 's eliminados' : ' eliminado'} exitosamente`
      );
    } else if (borrados.length === 0) {
      await AlertService?.error(
        'No se eliminó ninguno',
        `Fallaron los ${fallidos.length} intentos. Revisa si alguno es tu propio usuario o si ya fue eliminado.`
      );
    } else {
      await AlertService?.error(
        'Eliminación parcial',
        `Se eliminaron ${borrados.length} y fallaron ${fallidos.length}. Los que fallaron siguen seleccionados.`
      );
    }
  };

  // Get current user data
  const currentUser = authService?.getCurrentUser();
  const currentUserId = currentUser?.id;

  // El propio usuario nunca entra a la selección: el backend rechaza borrarlo.
  const seleccionables = filteredAndSortedUsers?.filter(u => u?.id !== currentUserId) || [];

  const handleSelectUser = (userId) => {
    if (userId === currentUserId) return;
    setSelectedUsers(prev =>
      prev?.includes(userId)
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const isAllSelected = seleccionables.length > 0 &&
    selectedUsers?.length === seleccionables.length;

  const handleSelectAll = () => {
    setSelectedUsers(isAllSelected ? [] : seleccionables.map(user => user?.id));
  };

  const contadores = [
    { label: 'Super Admins', value: users?.length ?? 0 },
    { label: 'Activos', value: users?.filter(user => user?.is_active)?.length ?? 0, tone: 'ok' },
    { label: 'Mostrados', value: filteredAndSortedUsers?.length ?? 0 },
  ];

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>Gestión de Super Administradores - PaySlip</title>
        <meta
          name="description"
          content="Gestione los usuarios Super Administradores del sistema"
        />
      </Helmet>

      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 16,
        }}
      >
        {loading && users.length === 0 ? (
          <div className="a2-counters">
            {contadores.map((c) => (
              <div key={c.label}>
                <Skel w={54} h={26} />
                <Skel w={78} h={11} style={{ marginTop: 6 }} />
              </div>
            ))}
          </div>
        ) : (
          <Counters items={contadores} />
        )}
        <Button icon="person_add" onClick={() => setShowCreateModal(true)} style={{ marginLeft: 'auto' }}>
          Agregar Super Admin
        </Button>
      </div>

      <Card pad={false}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ marginBottom: 14, minWidth: 0 }}>
            <h3 className="a2-card-title">Super Administradores</h3>
            <p className="a2-card-sub">Usuarios con acceso total al sistema</p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <SearchInput
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              placeholder="Buscar por nombre o correo…"
              aria-label="Buscar Super Admin"
              style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 9, background: 'var(--surface-2)' }}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              aria-label="Filtrar por estado"
              style={{ width: 'auto', minWidth: 165, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e?.target?.value)}
              aria-label="Ordenar"
              style={{ width: 'auto', minWidth: 160, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </div>
        </div>

        {/* Barra de selección: solo aparece cuando hay algo seleccionado */}
        <UserActionsToolbar
          selectedCount={selectedUsers?.length}
          onBulkDelete={handleBulkDelete}
          onClearSelection={() => setSelectedUsers([])}
        />

        <SystemUsersList
          users={filteredAndSortedUsers}
          selectedUsers={selectedUsers}
          currentUserId={currentUserId}
          isAllSelected={isAllSelected}
          onSelectAll={handleSelectAll}
          onSelectUser={handleSelectUser}
          onDeleteUser={handleDeleteUser}
          onResetPassword={handleResetPassword}
          loading={loading}
          emptyStateProps={{
            hasFilters: Boolean(searchTerm) || statusFilter !== 'all',
            onCreateNew: () => setShowCreateModal(true)
          }}
        />
      </Card>

      {showCreateModal && (
        <CreateUserModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateUser}
          isLoading={isCreatingUser}
        />
      )}
    </div>
  );
};

export default SystemUserManagement;
